import PropTypes from "prop-types"
import React, { useEffect, useState, useMemo, useRef } from "react"
import {
  Col,
  Row,
  Container,
  Button,
  Modal,
  ModalBody,
  Alert,
  Spinner,
} from "reactstrap"

import Breadcrumbs from "components/Common/Breadcrumb2"
import CampaignPermissionNotice from "components/Common/CampaignPermissionNotice"
import { withTranslation } from "react-i18next"
import "components/Common/ts-css.css"

import { get, post, put } from "helpers/api_helper"
import { getApiErrorMessage } from "helpers/api_error_helper"
import { normalizeCampaignContentBlocks, getSurveyEntryChannel } from "helpers/line_campaign_helper"
import useCurrentPermissions from "hooks/useCurrentPermissions"

import * as Yup from "yup"
import { useFormik } from "formik"

import { useNavigate, useParams } from "react-router-dom"
import moment from "moment"
import { toast } from "react-toastify"

// Step Components
import Step1 from "./components/Step1"
import Step2 from "./components/Step2"

const CancelModal = ({ show, onConfirm, onCancel }) => (
  <Modal size="md" isOpen={show} toggle={onCancel} centered>
    <ModalBody className="px-4 py-4">
      <h5 className="mb-4" style={{ fontWeight: "600" }}>Are you sure to cancel?</h5>
      <div className="d-flex gap-2 justify-content-end mt-4">
        <button
          type="button"
          className="btn btn-outline-secondary"
          style={{
            minWidth: "100px",
            backgroundColor: "#fff",
            border: "1px solid #ced4da",
            color: "#495057",
            borderRadius: "8px",
          }}
          onClick={onConfirm}
        >
          Yes
        </button>
        <button
          type="button"
          className="btn btn-success"
          style={{
            minWidth: "100px",
            backgroundColor: "#34c38f",
            border: "1px solid #34c38f",
            borderRadius: "8px",
          }}
          onClick={onCancel}
        >
          No
        </button>
      </div>
    </ModalBody>
  </Modal>
)

const SurveyLineCampaignTemplate = ({ pageView = "create" }) => {
  const navigate = useNavigate()
  const { id } = useParams()

  const [isLoading, setIsLoading] = useState(true)
  const [currentStep, setCurrentStep] = useState(1)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [loadError, setLoadError] = useState("")
  const [isSaving, setIsSaving] = useState(false)
  const saveLock = useRef(false)
  const campaignSaved = useRef(false)
  const requestedStatus = useRef("draft")
  const permissions = useCurrentPermissions()
  const canSave = permissions.hasScope(`survey-line-campaign:${pageView === "create" ? "create" : "update"}`)
  
  // States
  const [categoryOptions, setCategoryOptions] = useState([])
  const [allSurveyOptions, setAllSurveyOptions] = useState([])
  const [lineOaOptions, setLineOaOptions] = useState([])


  const validationSchema = Yup.object({
    lineOaId: Yup.string().required("Please Select LINE OA"),
    categoryId: Yup.string().required("Please Select Category"),
    surveyId: Yup.string().required("Please Select Survey"),
    recipientsLabel: Yup.string().when("broadcastTimeType", {
      is: "none",
      then: schema => schema,
      otherwise: schema => schema.max(100, "Max 100 characters"),
    }),
    specificUserLineUids: Yup.array(),
    scheduleDate: Yup.string().test("schedule-date", "Please Select a Valid Schedule Date", function (value) {
      return requestedStatus.current !== "schedule" || this.parent.broadcastTimeType !== "schedule" || moment(value, "YYYY-MM-DD", true).isValid()
    }),
    scheduleTime: Yup.string().test("schedule-time", "Please Select a Valid Schedule Time", function (value) {
      return requestedStatus.current !== "schedule" || this.parent.broadcastTimeType !== "schedule" || /^([01]\d|2[0-3]):[0-5]\d$/.test(value || "")
    }),
    startDate: Yup.date()
      .required("Please Select Start Date")
      .min(moment().startOf('day'), "Start date must be today or later"),
    endDate: Yup.date()
      .required("Please Select End Date")
      .min(Yup.ref("startDate"), "End date must be after start date"),
  })

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: ["create"].includes(pageView) ? undefined : "",
      lineOaId: "",
      categoryId: "",
      surveyId: "",
      surveyUrl: "",
      surveyMobileUrl: "",
      recipients: "all",
      recipientsLabel: "",
      audienceGender: [],
      audienceAge: [],
      audienceOs: [],
      audienceFriendshipPeriod: [],
      audienceCity: [],
      broadcastTimeType: "now",
      scheduleDate: "",
      scheduleTime: "",
      contentBlocks: [
        { id: "block-1", type: "text", textContent: "", imageUrl: "", imageTitle: "", imageAlt: "" },
      ],
      startDate: "",
      endDate: "",
      campaignName: "",
      specificUserLineUids: [],
      specificUserFileName: "",
      status: "draft",
    },
    validationSchema: validationSchema,
    onSubmit: async values => {
      if (!canSave || loadError) return
      const linkOnly = values.broadcastTimeType === "none"
      const allRecipients = linkOnly || values.recipients === "all"
      const normalizedContentBlocks = linkOnly ? [] : normalizeCampaignContentBlocks(values.contentBlocks)

      const payload = {
        line_oa_id: values.lineOaId ? Number(values.lineOaId) : null,
        campaign_name: values.campaignName,
        category_id: values.categoryId ? Number(values.categoryId) : null,
        survey_id: values.surveyId ? Number(values.surveyId) : null,
        survey_url: values.surveyUrl || "",
        recipients: linkOnly ? "all" : (values.recipients || "targeting"),
        recipients_label: allRecipients ? "" : (values.recipientsLabel || ""),
        audience_gender: allRecipients ? [] : (values.audienceGender || []),
        audience_age: allRecipients ? [] : (values.audienceAge || []),
        audience_os: allRecipients ? [] : (values.audienceOs || []),
        audience_friendship_period: allRecipients ? [] : (values.audienceFriendshipPeriod || []),
        audience_city: allRecipients ? [] : (values.audienceCity || []),
        broadcast_time_type: values.broadcastTimeType || "now",
        schedule_date: !linkOnly && values.scheduleDate ? moment.utc(values.scheduleDate).format("YYYY-MM-DD") : null,
        schedule_time: linkOnly ? null : (values.scheduleTime || null),
        content_blocks: normalizedContentBlocks,
        specific_user_line_uids: linkOnly ? [] : (values.specificUserLineUids || []),
        specific_user_file_name: linkOnly ? "" : (values.specificUserFileName || ""),
        start_date: values.startDate ? moment.utc(values.startDate).toISOString() : null,
        end_date: values.endDate ? moment.utc(values.endDate).toISOString() : null,
        status: requestedStatus.current,
      }

      try {
        let result
        if (["create"].includes(pageView)) {
          result = await post(
            process.env.REACT_APP_BACKEND_URL + "/v1/admin/line-campaign",
            payload
          )
        } else {
          result = await put(
            process.env.REACT_APP_BACKEND_URL +
              "/v1/admin/line-campaign/" +
              id,
            payload
          )
        }
        campaignSaved.current = true
        if (result?.broadcast?.success === false) {
          const error = result.broadcast.error
          const message = typeof error === "string" ? error : error?.message
          toast.error(`Campaign saved, but LINE sending failed.${message ? ` ${message}` : " Check the campaign status for details."}`)
        } else if (linkOnly && requestedStatus.current === "publish") {
          toast.success("Campaign published. No LINE message was sent.")
        } else {
          toast.success(pageView === "create" ? "Campaign created successfully" : "Campaign updated successfully")
        }
        
        const savedId = result?.data?.id || (pageView === "update" ? id : null)
        navigate(linkOnly && requestedStatus.current === "publish" && savedId
          ? `/survey-line-campaign/view/${savedId}`
          : "/survey-line-campaign")
      } catch (error) {
        toast.error(getApiErrorMessage(error, "Error saving campaign."))
      }
    },
  })

  // Step 1 Filtered Survey Options
  const filteredSurveyOptions = useMemo(() => {
    const selectedCategoryId = validation.values.categoryId
    if (!selectedCategoryId) return []

    const selectedCategory = categoryOptions.find(cat => String(cat.id) === String(selectedCategoryId))
    if (!selectedCategory) return []

    return allSurveyOptions.filter(survey => survey.category_name === selectedCategory.category_name)
  }, [validation.values.categoryId, categoryOptions, allSurveyOptions])

  // Get selected survey name
  const selectedSurveyName = useMemo(() => {
    const selectedSurveyId = validation.values.surveyId
    if (!selectedSurveyId) return ""

    const selectedSurvey = allSurveyOptions.find(survey => String(survey.id) === String(selectedSurveyId))
    return selectedSurvey ? selectedSurvey.name : ""
  }, [validation.values.surveyId, allSurveyOptions])

  const preFillForm = (formData = {}) => {
    const normalizedContentBlocks = Array.isArray(formData.content_blocks)
      ? formData.content_blocks.map(block => ({
          ...block,
          type: block?.type === "pictures" ? "image" : (block?.type || "text"),
        }))
      : []

    return validation.setValues({
      ...validation.values,
      id: formData.id,
      lineOaId: formData.line_oa_id || "",
      categoryId: formData.category_id || "",
      surveyId: formData.survey_id || "",
      surveyUrl: formData.survey_url || "",
      recipients: formData.recipients || "targeting",
      recipientsLabel: formData.recipients_label || "",
      audienceGender: Array.isArray(formData.audience_gender) ? formData.audience_gender : [],
      audienceAge: Array.isArray(formData.audience_age) ? formData.audience_age : [],
      audienceOs: Array.isArray(formData.audience_os) ? formData.audience_os : [],
      audienceFriendshipPeriod: Array.isArray(formData.audience_friendship_period) ? formData.audience_friendship_period : [],
      audienceCity: Array.isArray(formData.audience_city) ? formData.audience_city : [],
      broadcastTimeType: formData.broadcast_time_type || "now",
      scheduleDate: formData.schedule_date ? moment.utc(formData.schedule_date).format("YYYY-MM-DD") : "",
      scheduleTime: formData.schedule_time || "",
      contentBlocks: normalizedContentBlocks.length > 0 ? normalizedContentBlocks : [
        { id: "block-1", type: "image", textContent: "", imageUrl: "", imageTitle: "", imageAlt: "" },
        { id: "block-2", type: "text", textContent: "", imageUrl: "", imageTitle: "", imageAlt: "" },
      ],
      startDate: formData.start_date ? moment.utc(formData.start_date).format("YYYY-MM-DD") : "",
      endDate: formData.end_date ? moment.utc(formData.end_date).format("YYYY-MM-DD") : "",
      campaignName: formData.campaign_name || "",
      specificUserLineUids: Array.isArray(formData.specific_user_line_uids) ? formData.specific_user_line_uids : [],
      specificUserFileName: formData.specific_user_file_name || "",
      status: formData.status || "draft",
    }, false)
  }

  useEffect(() => {
    setIsLoading(true)
    setLoadError("")
    const fetchData = async () => {
      try {
        const requestList = [
          get(process.env.REACT_APP_BACKEND_URL + "/v1/admin/dropdown/category"),
          get(process.env.REACT_APP_BACKEND_URL + "/v1/admin/dropdown/questionnaire"),
          get(process.env.REACT_APP_BACKEND_URL + "/v1/admin/line-oa/dropdown"),
        ]

        if (["create"].includes(pageView)) {
          requestList.push(
            get(process.env.REACT_APP_BACKEND_URL + "/v1/admin/line-campaign/next-id")
          )
        } else if (id) {
          requestList.push(get(process.env.REACT_APP_BACKEND_URL + "/v1/admin/line-campaign/" + id))
        }

        const [catResult, surveyResult, lineOaResult, campaignResult] = await Promise.all(requestList)

        if (catResult && catResult.data) setCategoryOptions(catResult.data)
        if (surveyResult && surveyResult.data) setAllSurveyOptions(surveyResult.data)
        if (lineOaResult && lineOaResult.data) setLineOaOptions(lineOaResult.data)
        if (["create"].includes(pageView) && campaignResult?.data?.next_id) {
          validation.setFieldValue("id", campaignResult.data.next_id)
        } else if (["update"].includes(pageView)) {
          await preFillForm(campaignResult.data)
        }
      } catch (error) {
        setLoadError(getApiErrorMessage(error, "Unable to load campaign data and options."))
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [pageView, id])

  // Clear surveyId when categoryId changes
  const [prevCategoryId, setPrevCategoryId] = useState("")
  useEffect(() => {
    if (prevCategoryId !== validation.values.categoryId && prevCategoryId !== "") {
      validation.setFieldValue("surveyId", "")
    }
    setPrevCategoryId(validation.values.categoryId)
  }, [validation.values.categoryId])

  const buildSurveyUrls = (surveyUuidLink, lineOaId, campaignId) => {
    const base = `${process.env.REACT_APP_FRONTWEB_URL}/pdpa/chat/${surveyUuidLink}`
    const selectedOa = lineOaOptions.find(o => String(o.id) === String(lineOaId))

    const lineParams = ["identity=line"]
    if (selectedOa && selectedOa.channel_id_login) {
      lineParams.push(`cid=${selectedOa.channel_id_login}`)
    }
    if (campaignId) {
      lineParams.push(`lcid=${campaignId}`)
    }
    const entryChannel = getSurveyEntryChannel(validation.values.surveyUrl, campaignId, surveyUuidLink)
    if (entryChannel) lineParams.push(`entry_channel=${entryChannel}`)
    const surveyUrl = lineParams.length > 0 ? `${base}?${lineParams.join("&")}` : base

    // A LINE campaign always requires the selected OA login and a verified
    // LINE UID, including when the link is opened on a mobile device.
    const surveyMobileUrl = surveyUrl

    return { surveyUrl, surveyMobileUrl }
  }

  const setSurveyUrls = (surveyUuidLink, lineOaId, campaignId) => {
    const { surveyUrl, surveyMobileUrl } = buildSurveyUrls(surveyUuidLink, lineOaId, campaignId)
    validation.setFieldValue("surveyUrl", surveyUrl)
    validation.setFieldValue("surveyMobileUrl", surveyMobileUrl)
  }

  // Auto-fill survey URLs when surveyId changes
  const [prevSurveyId, setPrevSurveyId] = useState("")
  useEffect(() => {
    if (prevSurveyId !== validation.values.surveyId && validation.values.surveyId !== "") {
      const selectedSurvey = allSurveyOptions.find(s => String(s.id) === String(validation.values.surveyId))
      if (selectedSurvey && selectedSurvey.uuid_link) {
        setSurveyUrls(selectedSurvey.uuid_link, validation.values.lineOaId, validation.values.id)
      }
    } else if (validation.values.surveyId === "") {
      validation.setFieldValue("surveyUrl", "")
      validation.setFieldValue("surveyMobileUrl", "")
    }
    setPrevSurveyId(validation.values.surveyId)
  }, [validation.values.surveyId, allSurveyOptions])

  // Auto-fill survey URLs from dropdown when in edit mode (after dropdown data is loaded)
  useEffect(() => {
    if (["update"].includes(pageView) && validation.values.surveyId && allSurveyOptions.length > 0) {
      const selectedSurvey = allSurveyOptions.find(s => String(s.id) === String(validation.values.surveyId))
      if (selectedSurvey && selectedSurvey.uuid_link) {
        setSurveyUrls(selectedSurvey.uuid_link, validation.values.lineOaId, validation.values.id)
      }
    }
  }, [pageView, allSurveyOptions, validation.values.surveyId])

  // Update survey URLs when lineOaId changes (to update cid param)
  useEffect(() => {
    if (validation.values.surveyId && validation.values.lineOaId) {
      const selectedSurvey = allSurveyOptions.find(s => String(s.id) === String(validation.values.surveyId))
      if (selectedSurvey && selectedSurvey.uuid_link) {
        setSurveyUrls(selectedSurvey.uuid_link, validation.values.lineOaId, validation.values.id)
      }
    }
  }, [validation.values.lineOaId, lineOaOptions])

  // Ensure create page URL includes lcid when next_id arrives after survey selection
  useEffect(() => {
    if (["create"].includes(pageView) && validation.values.id && validation.values.surveyId) {
      const selectedSurvey = allSurveyOptions.find(s => String(s.id) === String(validation.values.surveyId))
      if (selectedSurvey && selectedSurvey.uuid_link) {
        setSurveyUrls(selectedSurvey.uuid_link, validation.values.lineOaId, validation.values.id)
      }
    }
  }, [pageView, validation.values.id, validation.values.surveyId, validation.values.lineOaId, allSurveyOptions, lineOaOptions])

  const handleNextStep = async () => {
    if (currentStep < 2) {
      // Validate Step 1 fields before proceeding
      if (currentStep === 1) {
        const errors = await validation.validateForm()
        const step1Errors = ["lineOaId", "categoryId", "surveyId", "startDate", "endDate"].filter(field => errors[field])

        if (step1Errors.length > 0) {
          step1Errors.forEach(field => {
            validation.setFieldTouched(field, true)
          })
          return
        }
      }
      setCurrentStep(currentStep + 1)
    }
  }

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleSave = async status => {
    if (saveLock.current || campaignSaved.current || !canSave || loadError) return
    saveLock.current = true
    requestedStatus.current = status
    setIsSaving(true)
    try {
      const errors = await validation.validateForm()
      if (Object.keys(errors).length) {
        await validation.setTouched(Object.fromEntries(Object.keys(errors).map(field => [field, true])), false)
        setCurrentStep(1)
        return
      }
      await validation.submitForm()
    } finally {
      saveLock.current = false
      setIsSaving(false)
    }
  }

  const PageAction = pageView === "create" ? "Create" : "Edit"
  document.title = `Survey Line Campaign | ${PageAction} | ` + process.env.REACT_APP_CMS_TITLE

  if (isLoading) {
    return <Spinner color="primary" />
  }

  return (
    <div className="page-content">
      <Container fluid={true}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          title={"Survey Line Campaign"}
          breadcrumbItems={[
            { title: "Survey Line Campaign", link: "survey-line-campaign" },
            { title: `${PageAction} Campaign`, link: pageView },
          ]}
        />

        {loadError && <Alert color="danger">{loadError}</Alert>}
        <CampaignPermissionNotice
          permissions={permissions}
          deniedMessage={!canSave ? "You do not have permission to save this campaign." : ""}
        />

        {!loadError && <>
        {/* Step Indicator */}
        {currentStep === 1 ? (
          <h4 className="mb-4" style={{ fontWeight: "600" }}>
            {PageAction} Campaign <span style={{ color: "#34c38f" }}>{currentStep}</span><span style={{ color: "#74788d" }}>/2</span>
          </h4>
        ) : (
          <h4 className="mb-4" style={{ fontWeight: "600" }}>
            Preview Campaign <span style={{ color: "#34c38f" }}>2</span><span style={{ color: "#74788d" }}>/2</span>
          </h4>
        )}

        {/* Step 1: All Campaign Details */}
        {currentStep === 1 && (
          <Step1
            validation={validation}
            pageView={pageView}
            categoryOptions={categoryOptions}
            surveyOptions={filteredSurveyOptions}
            lineOaOptions={lineOaOptions}
          />
        )}

        {/* Step 2: Preview Campaign */}
        {currentStep === 2 && (
          <Step2
            validation={validation}
            pageView={pageView}
            surveyName={selectedSurveyName}
            categoryOptions={categoryOptions}
            lineOaOptions={lineOaOptions}
          />
        )}

        {/* Navigation Buttons */}
        {currentStep === 1 ? (
          <Row className="mt-4">
            <Col style={{ display: "flex", justifyContent: "space-between" }}>
              <div className="d-flex gap-2">
                <Button
                  type="button"
                  outline
                  color="secondary"
                  style={{ minWidth: "120px", backgroundColor: "#fff", borderColor: "#ced4da", color: "#495057" }}
                  onClick={() => setShowCancelModal(true)}
                >
                  <i className="bx bx-x me-1"></i>
                  Cancel
                </Button>
              </div>
              <div>
                <Button
                  type="button"
                  color="success"
                  className="btn btn-success"
                  onClick={handleNextStep}
                >
                  Next
                </Button>
              </div>
            </Col>
          </Row>
        ) : (
          <Row className="mt-4">
            <Col style={{ display: "flex", justifyContent: "space-between" }}>
              {/* Left Side: Back, Cancel (สไตล์สีขาวเหมือน Order Campaign Step4) */}
              <div className="d-flex gap-2">
                <Button
                  type="button"
                  outline
                  color="secondary"
                  style={{ minWidth: "120px", backgroundColor: "#fff", borderColor: "#ced4da", color: "#495057" }}
                  onClick={handlePrevStep}
                >
                  <i className="bx bx-arrow-back me-1"></i>
                  Back
                </Button>
                <Button
                  type="button"
                  outline
                  color="secondary"
                  style={{ minWidth: "120px", backgroundColor: "#fff", borderColor: "#ced4da", color: "#495057" }}
                  onClick={() => setShowCancelModal(true)}
                >
                  <i className="bx bx-x me-1"></i>
                  Cancel
                </Button>
              </div>

              {/* Right Side: Save Draft, Confirm */}
              <div className="d-flex gap-2">
                <Button
                  type="button"
                  style={{
                    backgroundColor: "#fff",
                    border: "1px solid #34c38f",
                    color: "#34c38f",
                    minWidth: "120px",
                  }}
                  onClick={() => handleSave("draft")}
                  disabled={isSaving || !canSave}
                >
                  <i className="bx bx-save me-1"></i>
                  {isSaving ? "Saving..." : "Save Draft"}
                </Button>
                <Button
                  type="button"
                  color="success"
                  style={{ minWidth: "120px" }}
                  onClick={() => handleSave(validation.values.broadcastTimeType === "none" ? "publish" : "schedule")}
                  disabled={isSaving || !canSave}
                >
                  <i className="bx bx-check me-1"></i>
                  {validation.values.broadcastTimeType === "none" ? "Publish campaign" : "Confirm"}
                </Button>
              </div>
            </Col>
          </Row>
        )}
        </>}

      </Container>

      {/* Cancel Confirmation Modal */}
      <CancelModal
        show={showCancelModal}
        onConfirm={() => {
          setShowCancelModal(false)
          navigate("/survey-line-campaign")
        }}
        onCancel={() => setShowCancelModal(false)}
      />
    </div>
  )
}

SurveyLineCampaignTemplate.propTypes = {
  t: PropTypes.any,
  pageView: PropTypes.string,
}

export default withTranslation()(SurveyLineCampaignTemplate)
